package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.CommandPaletteAction
import com.merkost.handpass.admin.contract.CommandPaletteIntent
import com.merkost.handpass.admin.contract.CommandPaletteState
import com.merkost.handpass.admin.contract.CommandResult
import com.merkost.handpass.admin.contract.CommandResultType
import com.merkost.handpass.admin.util.toAdminMessage
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.launch
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

@OptIn(FlowPreview::class)
class CommandPaletteContainer(private val repo: AdminRepositoryApi) : Container<CommandPaletteState, CommandPaletteIntent, CommandPaletteAction> {

    private val scope = MainScope()

    private val queryFlow = MutableSharedFlow<String>(replay = 1)

    override val store by lazyStore(CommandPaletteState(), scope) {
        configure { name = "CommandPaletteStore" }
        recover { _ -> null }
        reduce { intent ->
            when (intent) {
                CommandPaletteIntent.Close -> updateState { copy(isOpen = false, query = "", selectedIndex = 0) }
                CommandPaletteIntent.Toggle -> updateState {
                    if (isOpen) copy(isOpen = false, query = "", selectedIndex = 0)
                    else copy(isOpen = true, results = AsyncSection.Loaded(navigationResults()), selectedIndex = 0)
                }
                is CommandPaletteIntent.SetQuery -> {
                    updateState { copy(query = intent.value, selectedIndex = 0) }
                    queryFlow.tryEmit(intent.value)
                }
                is CommandPaletteIntent.TapResult -> {
                    action(CommandPaletteAction.Navigate(intent.result.path))
                    updateState { copy(isOpen = false, query = "", selectedIndex = 0) }
                }
                is CommandPaletteIntent.SetResults -> updateState {
                    val count = (intent.results as? AsyncSection.Loaded)?.value?.size ?: 0
                    copy(results = intent.results, selectedIndex = selectedIndex.coerceIn(0, maxOf(0, count - 1)))
                }
                is CommandPaletteIntent.MoveSelection -> updateState {
                    val items = (results as? AsyncSection.Loaded)?.value.orEmpty()
                    if (items.isEmpty()) copy(selectedIndex = 0)
                    else copy(selectedIndex = (selectedIndex + intent.delta).coerceIn(0, items.size - 1))
                }
                CommandPaletteIntent.ConfirmSelection -> withState {
                    val items = (results as? AsyncSection.Loaded)?.value.orEmpty()
                    val target = items.getOrNull(selectedIndex)
                    if (target != null) {
                        action(CommandPaletteAction.Navigate(target.path))
                        updateState { copy(isOpen = false, query = "", selectedIndex = 0) }
                    }
                }
            }
        }
    }

    init {
        scope.launch {
            queryFlow.debounce(250).distinctUntilChanged().collect { query ->
                runSearch(query)
            }
        }
    }

    private suspend fun runSearch(query: String) {
        if (query.isBlank()) {
            store.intent(CommandPaletteIntent.SetResults(AsyncSection.Loaded(navigationResults())))
            return
        }

        store.intent(CommandPaletteIntent.SetResults(AsyncSection.Loading))

        try {
            val users = repo.getUsers(page = 1, pageSize = 5, search = query)
            val listings = repo.getListings(page = 1, pageSize = 5, search = query)

            val results = mutableListOf<CommandResult>()
            users.data.forEach {
                results += CommandResult(
                    type = CommandResultType.User,
                    id = it.id,
                    title = it.displayName,
                    subtitle = it.email,
                    path = "/users/${it.id}",
                )
            }
            listings.data.forEach {
                results += CommandResult(
                    type = CommandResultType.Listing,
                    id = it.id,
                    title = it.title,
                    subtitle = it.sellerName,
                    path = "/listings/${it.id}",
                )
            }
            store.intent(CommandPaletteIntent.SetResults(AsyncSection.Loaded(results)))
        } catch (e: Throwable) {
            store.intent(CommandPaletteIntent.SetResults(AsyncSection.Failed(e.toAdminMessage())))
        }
    }

    private fun navigationResults(): List<CommandResult> = listOf(
        CommandResult(CommandResultType.Navigation, "nav-queue", "Queue", "Triage pending reports", "/"),
        CommandResult(CommandResultType.Navigation, "nav-users", "Users", "User directory", "/users"),
        CommandResult(CommandResultType.Navigation, "nav-listings", "Listings", "Listing directory", "/listings"),
        CommandResult(CommandResultType.Navigation, "nav-logs", "Logs", "Moderation log", "/logs"),
        CommandResult(CommandResultType.Navigation, "nav-stats", "Stats", "Dashboard stats", "/stats"),
    )
}
